import React, { useRef, useLayoutEffect } from "react";
import { gsap, prefersReducedMotion } from "../../lib/motion";

/**
 * Counts a numeric value up from 0 when it scrolls into view, preserving any
 * non-numeric prefix/suffix (e.g. "220+", "85%", "1,240"). Non-numeric values
 * (like "—") render unchanged.
 */
export const CountUp = ({ value, className = "", duration = 1.6, ...rest }) => {
  const ref = useRef(null);
  const raw = value == null ? "" : String(value);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const m = raw.match(/^(\D*?)([\d][\d.,]*)(.*)$/s);
    if (!m) {
      el.textContent = raw;
      return;
    }
    const [, prefix, numStr, suffix] = m;
    const hasComma = numStr.includes(",");
    const decimals = (numStr.split(".")[1] || "").length;
    const target = parseFloat(numStr.replace(/,/g, ""));
    if (!isFinite(target)) {
      el.textContent = raw;
      return;
    }
    const fmt = (n) => {
      let s = decimals ? n.toFixed(decimals) : Math.round(n).toString();
      if (hasComma) s = Number(s).toLocaleString("en-US", { maximumFractionDigits: decimals });
      return `${prefix}${s}${suffix}`;
    };

    if (prefersReducedMotion()) {
      el.textContent = fmt(target);
      return;
    }

    const obj = { n: 0 };
    el.textContent = fmt(0);
    const tween = gsap.to(obj, {
      n: target,
      duration,
      ease: "power2.out",
      onUpdate: () => {
        el.textContent = fmt(obj.n);
      },
      scrollTrigger: { trigger: el, start: "top 92%" },
    });

    return () => {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
    };
  }, [raw, duration]);

  return <span ref={ref} className={className} aria-label={raw} {...rest} />;
};

export default CountUp;
